<template>
  <div class="sidebar-layout">
    <el-scrollbar wrap-class="scrollbar-wrapper">
      <el-menu
        :unique-opened="true"
        :show-timeout="200"
        :default-active="$route.path"
        :collapse="isCollapse"
        mode="vertical"
        class="el-menu-vertical"
      >
        <!-- LOGO -->
        <el-menu-item
          index="/"
          class="menu-logo"
          @click="$router.push('/')"
        >
          <el-image
            v-if="isCollapse"
            src="/images/new-logo-rond-seul-transp.png"
            fit="contain"
            style="height:40px;width:40px"
          />
          <el-image
            v-else
            :src="
              isDark
                ? '/images/logo-prosoluce-new-transp-dm.png'
                : '/images/logo-prosoluce-new-transp.png'
            "
            fit="contain"
            style="height:45px"
          />
        </el-menu-item>

        <!-- MENU -->
        <sidebar-item
          v-for="route in routes"
          :key="route.path"
          :item="route"
          :base-path="route.path"
          :is-nest="isCollapse"
        />

        <!-- Menu Comptes Extranet -->
        <template v-if="isExtranet && extranetAccounts.length > 0">
          <el-divider style="margin: 4px 0;" />
          <el-sub-menu
            v-if="extranetAccounts.length > 1 && !isCollapse"
            index="extranet-accounts"
          >
            <template #title>
              <Icon
                icon="mdi:office-building"
                width="20"
              />
              <span>Comptes</span>
            </template>
            <el-menu-item
              v-for="account in extranetAccounts"
              :key="account.id"
              :index="extranetAccountPath(account)"
              :class="{ 'is-active': isActiveAccount(account) }"
              @click="goToAccount(account)"
            >
              <Icon
                :icon="account.is_group ? 'mdi:office-building-marker' : 'mdi:account'"
                width="18"
              />
              <template #title>
                <span>{{ account.nom_aff }}</span>
              </template>
            </el-menu-item>
          </el-sub-menu>
          <el-menu-item
            v-else-if="extranetAccounts.length === 1"
            :index="extranetAccountPath(extranetAccounts[0])"
            :class="{ 'is-active': isActiveAccount(extranetAccounts[0]) }"
            @click="goToAccount(extranetAccounts[0])"
          >
            <Icon
              icon="mdi:account"
              width="20"
            />
            <template #title>
              <span>{{ extranetAccounts[0].nom_aff }}</span>
            </template>
          </el-menu-item>
          <!-- Collapsed : icône seule avec tooltip -->
          <el-menu-item
            v-else-if="extranetAccounts.length > 1 && isCollapse"
            index="extranet-accounts-collapsed"
            @click="showAccountSelector"
          >
            <Icon
              icon="mdi:office-building"
              width="20"
            />
            <template #title>
              <span>Comptes</span>
            </template>
          </el-menu-item>
        </template>
      </el-menu>
    </el-scrollbar>

    <!-- Bouton signaler un bug -->
    <div
      class="bug-report-link"
      title="Signaler un bug"
      @click="showBugReport = true"
    >
      <bug-icon :size="18" />
      <span
        v-if="!isCollapse"
        class="bug-report-label"
      >Signaler un bug</span>
    </div>

    <bug-report-dialog v-model:visible="showBugReport" />
  </div>
</template>

<script>
import { ref } from "vue";
import { mapGetters } from "vuex";
import { ElMenu, ElScrollbar, ElImage, ElSubMenu, ElMenuItem, ElDivider } from "element-plus";
import { Icon } from "@iconify/vue";
import { menuTree } from "./menuTree";
import SidebarItem from "./SidebarItem";
import BugReportDialog from "@/components/BugReportDialog.vue";
import { useTheme } from "@/composables/useTheme";

export default {
    name: "SideIndex",
    components: { SidebarItem, ElMenu, ElScrollbar, ElImage, ElSubMenu, ElMenuItem, ElDivider, Icon, BugReportDialog },
    setup() {
        const { isDark } = useTheme();
        const showBugReport = ref(false);
        return {
            isDark,
            showBugReport
        };
    },
    computed: {
        ...mapGetters(["sidebar", "permission_routes", "isExtranet"]),
        routes() {
            return menuTree(this.permission_routes);
        },
        isCollapse() {
            return !this.sidebar.opened;
        },
        extranetAccounts() {
            return (this.$store.state.user.extranet_routes || []).map(r => ({
                id: r.id,
                nom_aff: r.nom_aff || r.name || `Compte ${r.id}`,
                is_group: r.is_group || false,
            }));
        },
    },
    methods: {
        extranetAccountPath(account) {
            return account.is_group
                ? `/extranet/group/${account.id}`
                : `/extranet/dash/${account.id}`;
        },
        isActiveAccount(account) {
            return this.$route.path === this.extranetAccountPath(account);
        },
        goToAccount(account) {
            const path = this.extranetAccountPath(account);
            if (this.$route.path !== path) {
                this.$router.push(path);
            }
        },
        showAccountSelector() {
            this.$router.push('/dashboard');
        },
    },
};
</script>

<style lang="scss" scoped>
/* ----------- GLOBAL SIDEBAR ----------- */

.sidebar-scroll {
    height: 100vh;
}

.el-menu-vertical {
    height: 100%;
    border-right: none;
}

/* ----------- LOGO ----------- */
.menu-logo .el-image {
    height: 100%;
    display: flex;
    align-items: center;
}
.menu-logo {
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    &:hover {
        background-color: transparent !important;
    }
}

/* Logo état étendu */
.logo-expanded {
    height: 45px;
    max-width: 160px;
    transform: translateY(-1px);
}

/* Logo état réduit */
.logo-collapsed {
    height: 40px;
    width: 40px;
}

/* ----------- SIDEBAR LAYOUT ----------- */
.sidebar-layout {
    display: flex;
    flex-direction: column;
    height: 100vh;
}
.sidebar-layout > .el-scrollbar {
    flex: 1;
    overflow: hidden;
}

/* ----------- BUG REPORT ----------- */
.bug-report-link {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 16px;
    color: var(--el-text-color-secondary);
    cursor: pointer;
    font-size: 12px;
    border-top: 1px solid var(--el-border-color-lighter);
    transition: color 0.2s, background-color 0.2s;
    white-space: nowrap;
    &:hover {
        color: var(--el-color-danger);
        background-color: var(--el-fill-color-light);
    }
}
.bug-report-label {
    overflow: hidden;
    text-overflow: ellipsis;
}

/* ----------- COLLAPSE FIX ----------- */

.el-menu--collapse .menu-logo {
    padding: 0;
}
</style>
<!--<style lang="sass">    
.el-menu-vertical:not(.el-menu--collapse)
    min-height: 100vh
</style>-->
